import { and, eq, getTableColumns, isNotNull } from "@-/com.npmjs.drizzle-orm";
import type { SQLiteColumn, SQLiteInsertValue } from "@-/com.npmjs.drizzle-orm/sqlite-core";
import type { SoftDeletableTable } from "@-/dev.improve.haniverse.database._lib.wrap-database";
import type { ActorDatabaseHandle, ActorTransaction } from "#types";

export type CreateOrReviveActorRowOutcome = "created" | "kept" | "revived";

/**
 * Bring the row with a derived id into existence (see `database.md`, "Clean baseline and
 * identity"): insert it when it is missing, bring it back when it is tombstoned, and leave a live
 * row exactly as it is.
 *
 * `values` is the row as it is first created: its identity and required columns, nothing the user
 * may change later. Write everything else with an UPDATE after this call. That split is what makes
 * two devices that create the same row converge: sync uploads this insert as "create it unless it
 * is already there", which can never overwrite the other device's row, and uploads the UPDATE as a
 * change to just the columns it names.
 *
 * A tombstoned row comes back as that same fresh row, with every other column reset. A deleted row
 * that returns is a new row to the user, so nothing from before its deletion may resurface.
 *
 * Never write a row of a synced table with `onConflictDoUpdate`: the engine logs it as an insert
 * of the whole row, so it uploads as a creation, not as the change it was meant to be.
 *
 * Call it inside a transaction when the caller writes more than this one row.
 */
export async function createOrReviveActorRow<TTable extends SoftDeletableTable>(
  database: Pick<ActorDatabaseHandle | ActorTransaction, "insert" | "update">,
  table: TTable,
  values: SQLiteInsertValue<TTable> & { id: string },
): Promise<CreateOrReviveActorRowOutcome> {
  const columns = getTableColumns(table);
  const idColumn = readColumn(columns, "id");
  const deletedAtColumn = readColumn(columns, "deletedAt");

  const inserted = await database
    .insert(table)
    .values(values)
    .onConflictDoNothing()
    .returning({ id: idColumn });
  if (inserted.length > 0) {
    return "created";
  }

  const fresh: Record<string, unknown> = {};
  for (const [key, column] of Object.entries(columns)) {
    if (key !== "id") {
      fresh[key] = readFreshValue(column, Reflect.get(values, key));
    }
  }
  const revived = await database
    .update(table)
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- built from this table's own columns above
    .set(fresh as TTable["$inferInsert"])
    .where(and(eq(idColumn, values.id), isNotNull(deletedAtColumn)))
    .returning({ id: idColumn });
  return revived.length > 0 ? "revived" : "kept";
}

function readColumn(columns: Record<string, SQLiteColumn>, key: string): SQLiteColumn {
  const column = columns[key];
  if (column === undefined) {
    throw new TypeError(`Actor table has no ${key} column`);
  }
  return column;
}

/** The value a column holds in a newly inserted row: the given one, else its default, else NULL. */
function readFreshValue(column: SQLiteColumn, given: unknown): unknown {
  if (given !== undefined) {
    return given;
  }
  if (column.defaultFn !== undefined) {
    return column.defaultFn();
  }
  if (column.default !== undefined) {
    return column.default;
  }
  if (column.notNull) {
    throw new TypeError(`A new ${column.name} value is required to revive this row`);
  }
  return null;
}
